import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { documentRequestByToken, getDOCUMENTREQUEST, listEMAILNOTIFICATIONS, getCERTIFICATE } from '../../graphql/queries';
import { createDOCUMENTUPLOAD, updateDOCUMENTREQUEST, createEMAILNOTIFICATIONS, createACTIVITY } from '../../graphql/mutations';
import { awsDateToUS } from '../../utils/dateUtils';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  LinearProgress,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Divider,
  Stack,
  Collapse,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Send as SendIcon,
  Lock as LockIcon,
  Assignment as CertificateIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfViewIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();

// File type icons
const getFileIcon = (fileType) => {
  if (fileType.includes('pdf')) return <PdfIcon />;
  if (fileType.includes('image')) return <ImageIcon />;
  if (fileType.includes('doc')) return <DocIcon />;
  return <FileIcon />;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const PublicUploadPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [files, setFiles] = useState({});
  const [annotations, setAnnotations] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [associatedCertificates, setAssociatedCertificates] = useState([]);
  const [loadingCertificates, setLoadingCertificates] = useState(false);
  const [viewingCertificate, setViewingCertificate] = useState(null);
  const [certificateViewerOpen, setCertificateViewerOpen] = useState(false);

  const steps = ['Verify Access', 'Upload Documents', 'Complete'];

  // Validate token on mount
  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    setLoading(true);
    try {
      console.log('Validating token:', token);
      // Query for document request by upload token (using API_KEY for public access)
      const response = await client.graphql({ 
        query: documentRequestByToken,
        variables: { 
          uploadToken: token,
          limit: 1 
        },
        authMode: 'apiKey'
      });
      
      console.log('Response from documentRequestByToken:', response);
      const items = response?.data?.documentRequestByToken?.items || [];
      
      if (items.length === 0) {
        console.log('No items found for token:', token);
        setTokenError('Invalid or expired link');
        setTokenValid(false);
        return;
      }
      
      const request = items[0];
      
      // Check if token has been used
      if (request.tokenUsed) {
        setTokenError('This link has already been used');
        setTokenValid(false);
        return;
      }
      
      // Check if token has expired
      if (request.tokenExpiresAt) {
        const expiryDate = new Date(request.tokenExpiresAt);
        if (expiryDate < new Date()) {
          setTokenError('This link has expired');
          setTokenValid(false);
          return;
        }
      }
      
      // Check if request status is valid
      if (request.status === 'COMPLETED' || request.status === 'CANCELLED') {
        setTokenError('This document request has been ' + request.status.toLowerCase());
        setTokenValid(false);
        return;
      }
      
      // Get the full document request with categories (using API_KEY for public access)
      const fullRequest = await client.graphql({
        query: getDOCUMENTREQUEST, 
        variables: { id: request.id },
        authMode: 'apiKey'
      });
      
      const requestData = fullRequest?.data?.getDOCUMENTREQUEST;
      
      if (!requestData || !requestData.categories) {
        setTokenError('Unable to load document request details');
        setTokenValid(false);
        return;
      }
      
      setTokenValid(true);
      setRequestData(requestData);
      setActiveStep(1);
      
      // Initialize expanded categories
      const expanded = {};
      requestData.categories.forEach(cat => {
        expanded[cat.category] = true;
      });
      setExpandedCategories(expanded);
      
      // Always load associated certificates if they exist
      await loadAssociatedCertificates(requestData);
      
    } catch (error) {
      console.error('Error validating token:', error);
      console.error('Error details:', error.message, error.errors);
      setTokenError('Unable to validate link. Please try again.');
      setTokenValid(false);
    } finally {
      setLoading(false);
    }
  };
  
  const loadAssociatedCertificates = async (documentRequest) => {
    setLoadingCertificates(true);
    console.log('Loading associated certificates from document request:', documentRequest);
    console.log('Document request associatedCertificateIds:', documentRequest.associatedCertificateIds);
    console.log('Document request certificateDetails:', documentRequest.certificateDetails);
    
    try {
      // Get certificate IDs and details directly from the document request
      const certificateIds = documentRequest.associatedCertificateIds || [];
      let certificateDetails = [];
      
      // Parse certificate details if it's a JSON string
      if (documentRequest.certificateDetails) {
        try {
          certificateDetails = typeof documentRequest.certificateDetails === 'string' 
            ? JSON.parse(documentRequest.certificateDetails)
            : documentRequest.certificateDetails;
          console.log('Parsed certificate details:', certificateDetails);
        } catch (error) {
          console.error('Error parsing certificate details:', error);
        }
      }
      
      console.log('Certificate IDs from request:', certificateIds);
      console.log('Number of certificate IDs:', certificateIds.length);
      
      // Load full certificate data for each ID
      const certificates = [];
      for (const certId of certificateIds) {
        try {
          console.log('Loading certificate:', certId);
          const certResponse = await client.graphql({
            query: getCERTIFICATE,
            variables: { id: certId },
            authMode: 'apiKey'
          });
          
          if (certResponse?.data?.getCERTIFICATE) {
            const cert = certResponse.data.getCERTIFICATE;
            console.log('Loaded certificate:', cert);
            
            // Merge with stored details if available
            const storedDetail = certificateDetails.find(d => d.id === certId) || {};
            certificates.push({
              ...cert,
              ...storedDetail
            });
          }
        } catch (error) {
          console.error('Error loading certificate:', certId, error);
          // Fall back to stored details if available
          const storedDetail = certificateDetails.find(d => d.id === certId);
          if (storedDetail) {
            certificates.push(storedDetail);
          }
        }
      }
      
      console.log('Final certificates array:', certificates);
      setAssociatedCertificates(certificates);
      
    } catch (error) {
      console.error('Error loading associated certificates:', error);
    } finally {
      setLoadingCertificates(false);
    }
  };

  const handleFileDrop = useCallback((acceptedFiles, category) => {
    const categoryFiles = files[category] || [];
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      category,
      progress: 0
    }));

    setFiles(prev => ({
      ...prev,
      [category]: [...categoryFiles, ...newFiles]
    }));
  }, [files]);

  const removeFile = (category, fileId) => {
    setFiles(prev => ({
      ...prev,
      [category]: prev[category].filter(f => f.id !== fileId)
    }));
    
    // Remove annotation if exists
    setAnnotations(prev => {
      const newAnnotations = { ...prev };
      delete newAnnotations[fileId];
      return newAnnotations;
    });
  };

  const handleAnnotationChange = (fileId, value) => {
    setAnnotations(prev => ({
      ...prev,
      [fileId]: value
    }));
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getTotalFileCount = () => {
    return Object.values(files).reduce((total, catFiles) => total + catFiles.length, 0);
  };

  const handleUpload = async () => {
    setUploading(true);
    setActiveStep(2);

    try {
      // Prepare files for presigned URLs
      const filesToUpload = [];
      for (const [category, categoryFiles] of Object.entries(files)) {
        for (const fileItem of categoryFiles) {
          filesToUpload.push({
            id: fileItem.id,
            name: fileItem.file.name,
            type: fileItem.file.type,
            category,
            file: fileItem.file
          });
        }
      }

      // Get presigned URLs from Lambda
      const presignResponse = await fetch('https://2ee9al6q8k.execute-api.us-east-1.amazonaws.com/dev/upload/document-presign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documentRequestId: requestData.id,
          files: filesToUpload
        })
      });

      if (!presignResponse.ok) {
        throw new Error('Failed to get upload URLs');
      }

      const { presignedUrls } = await presignResponse.json();

      // Upload files using presigned URLs
      for (const urlInfo of presignedUrls) {
        const fileItem = filesToUpload.find(f => f.id === urlInfo.fileId);
        if (!fileItem) continue;

        // Upload file to S3 using presigned URL
        const uploadResponse = await fetch(urlInfo.presignUrl, {
          method: 'PUT',
          body: fileItem.file,
          headers: {
            'Content-Type': fileItem.type || 'application/octet-stream'
          }
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${fileItem.name}`);
        }

        // Update progress
        setUploadProgress(prev => ({
          ...prev,
          [fileItem.id]: 100
        }));

        // Save upload record
        // Create DOCUMENTUPLOAD record
        const uploadInput = {
          id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
          documentRequestID: requestData.id,
          clientID: requestData.clientID,
          clientVendorID: requestData.clientVendorID,
          category: fileItem.category,
          fileName: fileItem.name,
          fileSize: fileItem.file.size,
          fileType: fileItem.type,
          s3Key: urlInfo.s3Key,
          s3Bucket: 'coiguard-storage-68bff28f30421-dev',
          uploadedAt: new Date().toISOString(),
          status: 'UPLOADED',
          annotation: annotations[fileItem.id] || null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        // Call API to create DOCUMENTUPLOAD record
        try {
          const uploadResult = await client.graphql({
            query: createDOCUMENTUPLOAD,
            variables: { input: uploadInput },
            authMode: 'apiKey'
          });
          console.log('File uploaded and saved:', uploadInput, uploadResult);
        } catch (uploadError) {
          console.error('Error saving upload record:', uploadError);
          console.error('Upload input was:', uploadInput);
          // Continue with other uploads even if one fails
        }
      }

      // Mark token as used and update status
      const updateInput = {
        id: requestData.id,
        tokenUsed: true,
        uploadedAt: new Date().toISOString(),
        status: 'UPLOADED',
        totalFilesUploaded: Object.values(files).flat().length,
        updatedAt: new Date().toISOString()
      };
      
      // Call API to update DOCUMENTREQUEST status
      try {
        const updateResult = await client.graphql({
          query: updateDOCUMENTREQUEST,
          variables: { input: updateInput },
          authMode: 'apiKey'
        });
        console.log('Document request updated:', updateResult);

        // Log activity for document upload
        if (requestData.clientVendorID) {
          try {
            const uploadedCategories = Object.values(files).flat().map(f => f.category);
            const uniqueCategories = [...new Set(uploadedCategories)];

            await client.graphql({
              query: createACTIVITY,
              variables: {
                input: {
                  entity: 'VENDOR',
                  entityID: requestData.clientVendorID,
                  userFullName: requestData.vendorName || 'Vendor',
                  userID: 'vendor-upload',
                  action: 'DOCUMENT_CREATION',
                  field: JSON.stringify({
                    message: 'Other documents uploaded',
                    details: {
                      documentRequestID: requestData.id,
                      categories: uniqueCategories,
                      fileCount: Object.values(files).flat().length
                    }
                  })
                }
              },
              authMode: 'apiKey'
            });

            console.log('Document upload activity logged');
          } catch (activityError) {
            console.error('Failed to log document upload activity:', activityError);
          }
        }
      } catch (updateError) {
        console.error('Error updating document request:', updateError);
        console.error('Update input was:', updateInput);
      }

      // Create notification for upload completion
      const notificationInput = {
        id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
        recipients: [requestData.requestedByEmail],
        action: 'DOCUMENT_UPLOAD_COMPLETE',
        vendorName: requestData.vendorName,
        clientName: requestData.clientName || 'Client',
        subject: 'Documents Uploaded Successfully',
        content: `${requestData.vendorName} has uploaded the requested documents.`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Call API to create EMAILNOTIFICATIONS record
      try {
        const notificationResult = await client.graphql({
          query: createEMAILNOTIFICATIONS,
          variables: { input: notificationInput },
          authMode: 'apiKey'
        });
        console.log('Upload complete, notification created:', notificationInput, notificationResult);
      } catch (notificationError) {
        console.error('Error creating notification:', notificationError);
        console.error('Notification input was:', notificationInput);
      }
      
      // Mark upload as complete even if some database operations failed
      // The files are uploaded successfully to S3
      setUploadComplete(true);
    } catch (error) {
      console.error('Upload error:', error);
      // Check if any files were uploaded successfully
      const uploadedCount = Object.values(uploadProgress).filter(p => p === 100).length;
      if (uploadedCount > 0) {
        // Some files uploaded successfully
        alert(`${uploadedCount} file(s) uploaded successfully. Some operations may have failed. You can close this window.`);
        setUploadComplete(true);
      } else {
        // No files uploaded
        alert('Failed to upload files. Please try again.');
        setActiveStep(1);
      }
    } finally {
      setUploading(false);
    }
  };

  const CategoryDropzone = ({ category, validation }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop: (files) => handleFileDrop(files, category.category),
      accept: validation.acceptedFormats.reduce((acc, format) => {
        if (format === 'pdf') acc['application/pdf'] = ['.pdf'];
        if (format === 'doc') acc['application/msword'] = ['.doc'];
        if (format === 'docx') acc['application/vnd.openxmlformats-officedocument.wordprocessingml.document'] = ['.docx'];
        if (format === 'jpg' || format === 'jpeg') acc['image/jpeg'] = ['.jpg', '.jpeg'];
        if (format === 'png') acc['image/png'] = ['.png'];
        if (format === 'xls') acc['application/vnd.ms-excel'] = ['.xls'];
        if (format === 'xlsx') acc['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] = ['.xlsx'];
        return acc;
      }, {}),
      maxSize: validation.maxSizeMB * 1024 * 1024,
      maxFiles: validation.maxFiles - (files[category.category]?.length || 0)
    });

    const categoryFiles = files[category.category] || [];
    const isExpanded = expandedCategories[category.category];

    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={() => toggleCategory(category.category)}
          >
            <Box>
              <Typography variant="h6">
                {category.category.replace('_', ' ')}
                <Chip 
                  label={`${categoryFiles.length}/${validation.maxFiles}`}
                  size="small"
                  sx={{ ml: 2 }}
                  color={categoryFiles.length > 0 ? 'success' : 'default'}
                />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {category.description}
              </Typography>
            </Box>
            <IconButton size="small">
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={isExpanded}>
            <Box sx={{ mt: 2 }}>
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'grey.300',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <input {...getInputProps()} />
                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  {isDragActive ? 'Drop files here' : 'Drag & drop files here, or click to select'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Max {validation.maxFiles} files, {validation.maxSizeMB}MB each
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  Accepted: {validation.acceptedFormats.join(', ')}
                </Typography>
              </Box>

              {categoryFiles.length > 0 && (
                <List sx={{ mt: 2 }}>
                  {categoryFiles.map((fileItem) => (
                    <Paper key={fileItem.id} variant="outlined" sx={{ mb: 1, p: 2 }}>
                      <ListItem disablePadding>
                        <Box sx={{ mr: 2 }}>
                          {getFileIcon(fileItem.file.type)}
                        </Box>
                        <ListItemText
                          primary={fileItem.file.name}
                          secondary={formatFileSize(fileItem.file.size)}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => removeFile(category.category, fileItem.id)}
                            disabled={uploading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Add notes about this file (optional)"
                        value={annotations[fileItem.id] || ''}
                        onChange={(e) => handleAnnotationChange(fileItem.id, e.target.value)}
                        sx={{ mt: 2 }}
                        disabled={uploading}
                      />
                      
                      {uploadProgress[fileItem.id] > 0 && (
                        <LinearProgress 
                          variant="determinate" 
                          value={uploadProgress[fileItem.id]} 
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Paper>
                  ))}
                </List>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!tokenValid) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Invalid or Expired Link
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {tokenError || 'This upload link is no longer valid.'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please contact the requester for a new link if you need to upload documents.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (uploadComplete) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Upload Complete!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Your documents have been successfully uploaded and are now being reviewed.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can close this window.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Logo Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          mb: 3,
          p: 2,
          bgcolor: 'white'
        }}>
          <img 
            src="/app-logo-h110.png" 
            alt="MANACERT" 
            style={{ 
              height: '120px',
              objectFit: 'contain'
            }} 
          />
        </Box>

        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h4" gutterBottom>
                Document Upload
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {requestData?.clientName} has requested documents from {requestData?.vendorName}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign={{ xs: 'left', md: 'right' }}>
                <Chip 
                  icon={<LockIcon />} 
                  label="Secure Upload" 
                  color="success" 
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" display="block" color="text.secondary">
                  This link expires in 7 days
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Progress Stepper */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Main Content */}
        {activeStep === 1 && (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Instructions</AlertTitle>
              Please upload the requested documents below. You can add notes to each file if needed.
              Maximum 10 files total, up to 100MB per file.
            </Alert>
            
            {/* Associated Certificates Section - PROMINENT DISPLAY */}
            {loadingCertificates && (
              <Paper elevation={1} sx={{ p: 3, mb: 3, textAlign: 'center' }}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body1" component="span">
                  Loading associated certificates...
                </Typography>
              </Paper>
            )}
            
            {!loadingCertificates && associatedCertificates.length === 0 && requestData?.associatedCertificateIds?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <AlertTitle>Certificate Information</AlertTitle>
                Unable to load the associated certificates. Please contact the requester if you need certificate details.
              </Alert>
            )}
            
            {!loadingCertificates && associatedCertificates.length > 0 && (
              <Paper 
                elevation={3} 
                sx={{ 
                  mb: 3, 
                  border: '2px solid',
                  borderColor: 'primary.main',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'white', 
                  p: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <ViewIcon sx={{ mr: 2, fontSize: 28 }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      IMPORTANT: Review Related Certificate Before Uploading
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      The documents you upload must relate to this specific certificate
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ p: 3, bgcolor: '#f5f5f5' }}>
                  {associatedCertificates.map((cert, index) => (
                    <Card key={cert.id} variant="outlined" sx={{ mb: index < associatedCertificates.length - 1 ? 2 : 0 }}>
                      <CardContent>
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                            <CertificateIcon sx={{ mr: 1 }} />
                            Certificate Details
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<ViewIcon />}
                              onClick={() => {
                                // For public pages, show certificate details instead of PDF
                                // PDF files are protected and not accessible via API key auth
                                setViewingCertificate(cert);
                                setCertificateViewerOpen(true);
                              }}
                              sx={{ 
                                bgcolor: 'primary.main',
                                '&:hover': { bgcolor: 'primary.dark' }
                              }}
                            >
                              View Certificate Details
                            </Button>
                            <Chip 
                              label={cert.status} 
                              color={cert.status === 'APPROVED' ? 'success' : 'default'}
                              sx={cert.status === 'REVIEW' ? {
                                backgroundColor: 'rgba(128, 0, 128, 0.1)',
                                color: 'purple',
                                fontWeight: 'bold'
                              } : { fontWeight: 'bold' }}
                            />
                          </Box>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                              INSURED NAME
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                              {cert.insuredName || cert.insured || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                              CERTIFICATE HOLDER
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 2 }}>
                              {cert.certificateHolder || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                              LAST MODIFIED
                            </Typography>
                            <Typography variant="body2">
                              {cert.updatedAt ? awsDateToUS(cert.updatedAt) : '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                              EXPIRATION DATE
                            </Typography>
                            <Typography variant="body2">
                              {cert.firstExp && cert.firstExp !== '0000-01-01' ? awsDateToUS(cert.firstExp) : '-'}
                            </Typography>
                          </Grid>
                          {cert.policyNumber && (
                            <Grid item xs={12} md={4}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                                POLICY NUMBER
                              </Typography>
                              <Typography variant="body2">
                                {cert.policyNumber}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Paper>
            )}
            
            {loadingCertificates && (
              <Paper elevation={1} sx={{ p: 3, mb: 3, textAlign: 'center' }}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body2" component="span">
                  Loading related certificates...
                </Typography>
              </Paper>
            )}

            {/* Categories */}
            {requestData?.categories?.map((category) => {
              const validation = requestData.validations?.find(v => v.category === category.category) || {
                acceptedFormats: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
                maxFiles: 5,
                maxSizeMB: 100
              };

              return (
                <CategoryDropzone
                  key={category.category}
                  category={category}
                  validation={validation}
                />
              );
            })}

            {/* Upload Button */}
            <Paper elevation={1} sx={{ p: 3, mt: 3, position: 'sticky', bottom: 20 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="body1">
                    Total Files: {getTotalFileCount()} / 10
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4} textAlign="right">
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SendIcon />}
                    onClick={handleUpload}
                    disabled={getTotalFileCount() === 0 || uploading}
                    fullWidth={{ xs: true, md: false }}
                  >
                    {uploading ? 'Uploading...' : 'Submit Documents'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}

        {/* Upload Progress */}
        {activeStep === 2 && !uploadComplete && (
          <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Uploading Documents...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please do not close this window
            </Typography>
          </Paper>
        )}
      </Container>
      
      {/* Certificate Details Modal */}
      {viewingCertificate && (
        <Dialog
          open={certificateViewerOpen}
          onClose={() => {
            setCertificateViewerOpen(false);
            setViewingCertificate(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6">Certificate Details</Typography>
            <IconButton onClick={() => {
              setCertificateViewerOpen(false);
              setViewingCertificate(null);
            }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info">
                  <AlertTitle>Certificate Information</AlertTitle>
                  Please ensure your uploaded documents relate to this certificate.
                </Alert>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  INSURED NAME
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewingCertificate.insuredName || viewingCertificate.insured || '-'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  CERTIFICATE HOLDER
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewingCertificate.certificateHolder || '-'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  STATUS
                </Typography>
                <Chip 
                  label={viewingCertificate.status} 
                  color={viewingCertificate.status === 'APPROVED' ? 'success' : 'default'}
                  sx={viewingCertificate.status === 'REVIEW' ? {
                    backgroundColor: 'rgba(128, 0, 128, 0.1)',
                    color: 'purple'
                  } : {}}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  CERTIFICATE DATE
                </Typography>
                <Typography variant="body2">
                  {viewingCertificate.certificateDate ? awsDateToUS(viewingCertificate.certificateDate) : '-'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  EXPIRATION DATE
                </Typography>
                <Typography variant="body2">
                  {viewingCertificate.firstExp && viewingCertificate.firstExp !== '0000-01-01' 
                    ? awsDateToUS(viewingCertificate.firstExp) 
                    : '-'}
                </Typography>
              </Grid>
              
              {viewingCertificate.policyNumber && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    POLICY NUMBER
                  </Typography>
                  <Typography variant="body2">
                    {viewingCertificate.policyNumber}
                  </Typography>
                </Grid>
              )}
              
              {viewingCertificate.producer && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    PRODUCER
                  </Typography>
                  <Typography variant="body2">
                    {viewingCertificate.producer}
                  </Typography>
                </Grid>
              )}
              
              {viewingCertificate.projectName && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    PROJECT
                  </Typography>
                  <Typography variant="body2">
                    {viewingCertificate.projectName}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
            <Button 
              onClick={() => {
                setCertificateViewerOpen(false);
                setViewingCertificate(null);
              }}
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default PublicUploadPage;